import axios from 'axios';
import History from '../history';
import { JWTPayload } from 'features/Authentication/slice/types';
import React from 'react';
import { error } from 'core/components/Notify';
import { checkDateFormatAndConvert } from './DateTime';
import { BranchHoliday } from '../types/modules/org';
import isEmpty from "lodash/isEmpty";
import EncryptedStorage from 'core/utils/constants/encryptedStorage';
import { getAuthAccessData } from 'core/utils/accessControl';

export const PAGE_SIZE = 'page_size=500&page_index=1';

export const getCurrentYear = () => {
  return new Date().getFullYear();
};

export function addDotToTemplateParameters(data) {
  // Regular expression to match placeholders like {{1}}, {{2}}, etc. at the end of string
  const placeholderPattern = /\{\{\d+\}\}$/;

  return data.map(item => {
    // Create a deep copy to avoid mutating the original data
    const updatedItem = JSON.parse(JSON.stringify(item));

    if (updatedItem.PARAMETERS && Array.isArray(updatedItem.PARAMETERS)) {
      updatedItem.PARAMETERS = updatedItem.PARAMETERS.map(param => {
        // Check if it's a header parameter with TEXT format
        if (param.type === 'header' && param.format === 'TEXT' && param.text) {
          if (placeholderPattern.test(param.text)) {
            param.text = param.text + '.';
          }
        }

        // Check if it's a body parameter
        if (param.type === 'body' && param.text) {
          if (placeholderPattern.test(param.text)) {
            param.text = param.text + '.';
          }
        }

        return param;
      });
    }

    return updatedItem;
  });
}


export function convertWahtsAppTemplatePayloadIntoRequiredPayload(currentPayload) {
  const name = currentPayload.name || "";
  const language = currentPayload.language || "";
  const category = currentPayload.category || "";
  const components = currentPayload.components || [];

  let parameters = components;
  let headerText = "";
  let bodyText = "";

  components.forEach((comp) => {
    // HEADER processing
    if (comp.type.toLowerCase() === "header") {
      headerText = comp.text || "";
      const exampleHeader = comp.example?.header_text || [];
      if (exampleHeader.length > 0) {
        exampleHeader.forEach((val, idx) => {
          const regex = new RegExp(`\\{\\{${idx + 1}\\}\\}`, "g");
          headerText = headerText.replace(regex, val);
        });
      }
    }

    // BODY processing
    if (comp.type.toLowerCase() === "body") {
      bodyText = comp.text || "";
      const exampleBody = comp.example?.body_text || [[]];
      if (exampleBody && exampleBody[0]) {
        exampleBody[0].forEach((val, idx) => {
          const regex = new RegExp(`\\{\\{${idx + 1}\\}\\}`, "g");
          bodyText = bodyText.replace(regex, val);
        });
      }
    }
  });

  // Combine header + body
  const sampleWhatsApp = `${headerText ? headerText + " \n " : ""}${bodyText}`;

  // Build required payload
  const requiredPayload = [
    {
      BRANCH_ID: currentPayload?.BRANCH_ID,
      COMMUNICATION_MODULE_ID: currentPayload?.COMMUNICATION_MODULE_ID,
      NAME: name,
      DESCRIPTION: currentPayload?.DESCRIPTION,
      LANGUAGE: language,
      CATEGORY: category,
      SAMPLE_WHATSAPP: sampleWhatsApp,
      PARAMETERS: parameters,
      TEMPLATE_STATUS: "PENDING",
    },
  ];

  return requiredPayload;
}



export function convertKeysToLowercase(originalObject) {
  // Start by creating a deep copy to ensure the original object is not modified (immutability).
  const newObject = JSON.parse(JSON.stringify(originalObject));

  // // 1. Convert the top-level 'category' value to lowercase.
  // if (newObject.category && typeof newObject.category === 'string') {
  //     newObject.category = newObject.category.toLowerCase();
  // }

  // 2. Iterate through the 'components' array and convert 'type' values to lowercase.
  if (newObject.components && Array.isArray(newObject.components)) {
    newObject.components.forEach(component => {
      // Convert component's 'type'
      if (component.type && typeof component.type === 'string') {
        component.type = component.type.toLowerCase();
      }

      // Convert nested 'type' values inside the 'buttons' array, if present.
      if (component.buttons && Array.isArray(component.buttons)) {
        component.buttons.forEach(button => {
          if (button.type && typeof button.type === 'string') {
            button.type = button.type.toLowerCase();
          }
        });
      }
    });
  }

  return newObject;
}

export const parseJSON = (data) => {
  try {
    if (!isEmpty(data)) {
      return JSON.parse(data) || "";
    }
    return ""
  } catch (err) {
    return "";
  }
}

export function playBellDing() {
  const ctx = new (window.AudioContext || (window as any)?.webkitAudioContext)();

  // Main tone (bell-like)
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(880, ctx.currentTime); // A5

  // Harmonic overtone for richness
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(1320, ctx.currentTime); // E6

  // Volume control
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1);

  // Connect oscillators
  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(ctx.destination);

  // Play and stop
  osc1.start();
  osc2.start();
  osc1.stop(ctx.currentTime + 1);
  osc2.stop(ctx.currentTime + 1);
}

export const getUniqueId = (() => {
  let uniqueId: string | null = null;

  return () => {
    if (!uniqueId) {
      uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    return uniqueId;
  };
})();

export const getImageUrl = (url: string) => {
  const uniqueId = getUniqueId();
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}cache=${uniqueId}`;
};


export function isFileUpload(data) {
  // Check if data is FormData (most common for file uploads)
  if (data instanceof FormData) {
    return true;
  }

  // Check if data is a Blob or File
  if (data instanceof Blob || data instanceof File) {
    return true;
  }

  // Check if it's an ArrayBuffer or similar binary data
  if (data instanceof ArrayBuffer || data instanceof Uint8Array) {
    return true;
  }

  // Check if it's an object containing File or Blob properties
  if (data && typeof data === 'object') {
    for (const key in data) {
      if (data[key] instanceof File || data[key] instanceof Blob) {
        return true;
      }
      // Check for nested FormData
      if (data[key] instanceof FormData) {
        return true;
      }
    }
  }

  return false;
}

export function trimPayload(input) {
  if (input === null || input === undefined) {
    return undefined;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    return trimmed === '' ? undefined : trimmed;
  }

  if (typeof input === 'number' || typeof input === 'boolean') {
    return input;
  }

  if (Array.isArray(input)) {
    const trimmedArray = input
      .map(item => trimPayload(item))
      .filter(item => item !== undefined);
    return trimmedArray.length > 0 ? trimmedArray : undefined;
  }

  if (typeof input === 'object') {
    const trimmedObj = {};
    for (const key in input) {
      if (input.hasOwnProperty(key)) {
        const trimmedKey = key.trim();
        const trimmedValue = (key === "LAST_NAME") ? input?.[key] : trimPayload(input?.[key]);
        if (trimmedValue !== undefined) {
          trimmedObj[trimmedKey] = trimmedValue;
        }
      }
    }
    return Object.keys(trimmedObj).length > 0 ? trimmedObj : undefined;
  }

  return undefined;
}

export const openWindow = (url: string, target: string = '_blank', size?) => {
  const width = window.screen.availWidth;
  const height = window.screen.availHeight;

  window.open(
    url,
    target,
    `width=${width},height=${height},left=0,top=0`
  );
};

export function roundMarks(number) {
  const base = Math.floor(number);
  const decimal = number - base;

  if (decimal <= 0.25) {
    return base;
  } else if (decimal > 0.25 && decimal < 0.5) {
    return base + 0.5;
  } else if (decimal === 0.5) {
    return base + 0.5;
  } else if (decimal > 0.5 && decimal < 0.75) {
    return base + 0.5;
  } else {
    return base + 1;
  }
}

export function matchesPatternStartsWith(path, pattern) {
  // Remove trailing '*' and any trailing slash
  const base = pattern.replace(/\*$/, '').replace(/\/+$/, '');

  return path.startsWith(base + '/') || path === base;
}

export const colors = {
  white: "#FFFFFF",
  black: "#000000",
  red: "#FF0000",
  green: "#00FF00",
  blue: "#0000FF",
  yellow: "#FFFF00",
  cyan: "#00FFFF",
  magenta: "#FF00FF",
  gray: "#808080",
  orange: "#FFA500",
  purple: "#800080",
  pink: "#FFC0CB",
  brown: "#A52A2A",
  lime: "#00FF00",
  navy: "#000080",
  teal: "#008080",
  olive: "#808000",
  maroon: "#800000",
  silver: "#C0C0C0",
  gold: "#FFD700",
};

export function hasComma(str) {
  return /,/.test(str);
}

export const removeHtmlTags = (htmlString) => {
  return htmlString?.replace(/<[^>]*>/g, '');
};

export function replaceNewlinesWithBreaks(input) {
  return input.replace(/\n/g, '<br>');
}

export function mergeAndTransform(dynamicArray, staticArray) {
  // If dynamicArray is empty, return the staticArray as is
  if (!dynamicArray?.length) {
    return staticArray;
  }

  // Create a map of dynamicArray by the 'module' key
  const dynamicMap = new Map(dynamicArray?.map(item => [item?.module, item]));

  // Iterate over staticArray and update its boolean values based on dynamicArray
  const transformedArray = staticArray?.map(staticObj => {

    const dynamicObj: any = dynamicMap?.get(staticObj?.module);

    if (dynamicObj) {
      // Update top-level boolean values

      ['hasAccess', 'upsert', 'delete', 'view', 'mobile', 'email', 'aadhaar'/* , 'name' */]?.forEach(key => { // here name commented because now we need to relay on code level STATIC names

        if (dynamicObj?.[key] !== undefined) {
          staticObj[key] = dynamicObj?.[key];
        }

      });

      // If subModules exist, map through and update based on route
      if (staticObj?.subModules && dynamicObj?.subModules) {

        staticObj.subModules = staticObj?.subModules?.map(staticSubModule => {

          const dynamicSubModule = dynamicObj?.subModules?.find(
            subModule => subModule?.route === staticSubModule?.route
          );

          if (dynamicSubModule) {

            // Update subModule boolean values
            ['hasAccess', 'mobile', 'email', 'aadhaar', 'upsert', 'delete', 'view'/* , 'name' */]?.forEach(key => { // here name commented because now we need to relay on code level STATIC names
              if (dynamicSubModule?.[key] !== undefined) {
                staticSubModule[key] = dynamicSubModule?.[key];
              }
            });

          }


          return staticSubModule;
        });

      }

    }

    // Return the updated static object
    return staticObj;
  });

  return transformedArray;
}

export const getTomorrowDate = () => {
  var currentDate = new Date();

  currentDate.setDate(currentDate.getDate() + 1);

  var year = currentDate.getFullYear();
  var month = currentDate.getMonth() + 1;
  var day = currentDate.getDate();

  var tomorrowDate = year + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;

  return new Date(tomorrowDate);
}

export function formatString(input) {

  if (!input)
    return "";

  let result = '';

  const charactersToRemove = new Set([',', '₹', '$', '€', '£', '¥']);
  const removeNewLine = new Set(['\n']);

  for (let char of input) {
    if (charactersToRemove.has(char)) {
      result += "";
    } else if (removeNewLine.has(char)) {
      result += " | ";
    } else {
      result += char;
    }
  }

  return result?.trim();

  // return input
  //   .trim() // Remove leading and trailing spaces
  //   .replace(/\n+/g, ' | ') // Replace newlines with " | "
  //   .replace(/,+/g, '') // Replace commas with an empty string
  //   .replace(/[\$\£\€\¥\₹]/g, '') // Remove common currency symbols ($, £, €, ¥)
  //   .replace(/\s+/g, ' '); // Replace multiple spaces with a single space
}

function reorderDataForExcel(data, columns) {
  return data?.map(item => {
    let reorderedItem = {};
    columns?.forEach(col => {
      let key = col?.id || col?.accessorKey || col?.header || col?.Header;
      if (key && item.hasOwnProperty(key)) {
        reorderedItem[key] = item[key];
      }
    });
    return reorderedItem;
  }) || [];
}


export function transformDataForExcel(data, columns) {
  // Create a mapping from keys in data to headers in columns
  let keyToHeader = {};
  columns?.forEach(column => {
    if (column?.accessorKey) {
      keyToHeader[column?.accessorKey] = column?.header;
    } else if (column?.id) {
      keyToHeader[column?.id] = column?.header;
    }
  });
  // Transform the data array
  return reorderDataForExcel(data, columns)?.map((item, index) => {
    let transformedItem = { "S.No": index + 1 };
    for (let key in item) {
      if (keyToHeader?.[key]) {
        transformedItem[keyToHeader[key]] = checkDateFormatAndConvert(item?.[key]);
      }
    }
    return transformedItem;
  }) || [];
}

export function reorderTable(staticData, dynamicData) {
  // Filter out null values from staticData
  let filteredStaticData = staticData?.filter(item => item !== null);

  // Create a mapping of dynamicData for quick lookup
  let dynamicDataIndex = dynamicData?.reduce((acc, key, index) => {
    acc[key] = index;
    return acc;
  }, {});

  // Sort the staticData based on the dynamicData
  filteredStaticData?.sort((a, b) => {
    let aKey = a?.id || a?.accessorKey || a?.header;
    let bKey = b?.id || b?.accessorKey || b?.header;

    let aIndex = dynamicDataIndex?.[aKey];
    let bIndex = dynamicDataIndex?.[bKey];

    if (aIndex === undefined && bIndex === undefined) {
      return 0;
    }
    if (aIndex === undefined) {
      return 1;
    }
    if (bIndex === undefined) {
      return -1;
    }
    return aIndex - bIndex;
  });

  return (filteredStaticData || []);
}

export function verifyEmail(email) {
  // Regular expression for email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test the email against the regular expression
  return emailRegex.test(email);
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function formatMarks(marks: string, needDecimals: boolean): string {
  const range = marks?.split('-').map(num => num.trim());

  const formattedRange = range?.map(num => {
    const floatNum = parseFloat(num);

    if (needDecimals) {
      return floatNum.toFixed(2);
    }

    if (floatNum % 1 === 0) {
      return Math.floor(floatNum).toString();
    } else {
      return floatNum.toFixed(2);
    }
  });

  return formattedRange?.join('-') || '';
}


export function isAudioFile(file) {
  // Check if the input is a File object
  if (!(file instanceof File)) {
    return false;
  }

  // Define an array of valid audio MIME types
  const audioMimeTypes = [
    'audio/mpeg', // MP3
    'audio/wav',  // WAV
    'audio/ogg',  // OGG
    'audio/midi', // MIDI
    'audio/x-wav', // WAV
    'audio/webm', // WebM
    'audio/aac',  // AAC
    'audio/x-aac', // AAC
    'audio/x-m4a', // M4A
    'audio/flac',  // FLAC
    'audio/x-flac', // FLAC
  ];

  // Check if the file's MIME type is in the list of audio MIME types
  return audioMimeTypes.includes(file.type);
}

export function transformStudentListAsOldFormat(data) {
  const renderList = () => {
    if (!!data?.[0]?.Students?.length) {
      return data?.[0]?.Students;
    }
    return data;
  }
  if (renderList()?.length) {
    return renderList()?.map(item => ({
      ...item,
      StudentClass: {
        TRANSPORT_PICKUP_POINT_ID: item?.TRANSPORT_PICKUP_POINT_ID,
        ROLL_NUMBER: item?.ROLL_NUMBER,
        BRANCH_ID: item?.BRANCH_ID,
        FEE_PAYMENT_DURATION_ID: item?.FEE_PAYMENT_DURATION_ID,
        STUDENT_CLASS_ID: item?.STUDENT_CLASS_ID,
        STUDENT_CLASS_STATUS: item?.STUDENT_CLASS_STATUS,
        CLASS_ID: item?.CLASS_ID,
        STATUS: item?.STATUS,
        STUDENT_ID: item?.STUDENT_ID,
        ACADEMIC_YEAR_ID: item?.ACADEMIC_YEAR_ID,
        Class: {
          BRANCH_ID: item?.BRANCH_ID,
          FEE_PAYMENT_DURATION_ID: item?.FEE_PAYMENT_DURATION_ID,
          CLASS_CODE: item?.CLASS_CODE,
          STATUS: item?.STATUS,
          CLASS_ID: item?.CLASS_ID,
          NAME: item?.CLASS_NAME,
          MINDZIT_CLASS_CODE: item?.MINDZIT_CLASS_CODE
        }
      }
    }))
  }
  return [];
}

export function getFileType(url: any): any {
  const extension = url?.split('.')?.pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'image';
    case 'xlsx':
    case 'xls':
      return 'xlsx';
    default:
      return 'text';
  }
}


export const addPageSize = (url, TABLE_DATA?: { page_size?: string | number, page_index?: string | number }) => {
  if (!TABLE_DATA) {
    if (!url?.includes('page_size')) {
      if (url?.includes('?')) {
        return url + '&' + PAGE_SIZE;
      }
      return url + '?' + PAGE_SIZE;
    }
  } else {
    if (!url?.includes('page_size')) {
      if (url?.includes('?')) {
        return url + '&' + `page_size=${TABLE_DATA?.page_size}&page_index=${convertNaNIntoNumber(TABLE_DATA?.page_index) + 1}`;
      }
      return url + '?' + `page_size=${TABLE_DATA?.page_size}&page_index=${convertNaNIntoNumber(TABLE_DATA?.page_index) + 1}`;
    }
  }
  return url;
};

export const PHYSICAL_DISABILITY = [
  { label: 'YES', value: true },
  { label: 'NO', value: false }
];

export const GENDERS = [
  { label: 'MALE', value: 0 },
  { label: 'FEMALE', value: 1 },
  { label: 'OTHER', value: 2 },
];

export const RELATION = [
  { label: 'FATHER', value: 0 },
  { label: 'MOTHER', value: 1 },
  { label: 'GUARDIAN', value: 2 },
];

export const PRIORITY = [
  { label: 'CRITICAL', value: 0 },
  { label: 'HIGH', value: 1 },
  { label: 'NORMAL', value: 2 },
  { label: 'LOW', value: 3 },
];

export const COMMUNICATION_MODE = [
  { label: 'CALL', value: 'CALL' },
  { label: 'SMS', value: 'SMS' },
];

export const IS_RECURRING = [
  { label: 'YES', value: true },
  { label: 'NO', value: false },
];

export const IS_AUTOMATIC = [
  { label: 'YES', value: true },
  { label: 'NO', value: false },
];

export const IS_TRUE = [
  { label: 'YES', value: true },
  { label: 'NO', value: false },
];

export const DAYS_BEFORE_DUE_DATE = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
  { label: '7', value: 7 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10', value: 10 },
];

export const TRANSACTION_TYPES = {
  all: 'ALL',
  income: 'INCOME',
  expense: 'EXPENSE',
};

export const RESULT = [
  { NAME: 'NOT AVAILABLE', ID: 1, VALUE: 'NA' },
  { NAME: 'PASS', ID: 2, VALUE: 'PASS' },
  { NAME: 'FAIL', ID: 3, VALUE: 'FAIL' },
  { NAME: 'ABSENT', ID: 4, VALUE: 'ABSENT' },
];

export const RoleColors = (ROLE_ID: number) => {
  if (ROLE_ID === 1) {
    return 'bg-[#B7C3F3] text-black font-light border-[#B7C3F3] text-sm';
  } else if (ROLE_ID === 2) {
    return 'bg-[#7FB3D5] text-black font-light border-[#7FB3D5] text-sm';
  } else if (ROLE_ID === 3) {
    return 'bg-[#FFE0B2] text-black font-light border-[#FFE0B2] text-sm';
  } else if (ROLE_ID === 4) {
    return 'bg-[#FFB6C1] text-black font-light border-[#FFB6C1] text-sm';
  } else if (ROLE_ID === 5) {
    return 'bg-[#C8E6C9] text-black font-light border-[#C8E6C9] text-sm';
  }
  return '';
};

export const BranchEnquiryStatus = [
  { label: 'ENQUIRED', value: 0 },
  { label: 'NOT_INTERESTED', value: 1 },
  { label: 'ADMISSION', value: 2 },
  { label: 'HOT_LEAD', value: 3 },
];

export const ENQUIRY_TYPE = [
  { label: 'GENERAL', value: "GENERAL" },
  { label: 'ADMISSION', value: "ADMISSION" },
];

export const LeaveStatus = [
  { label: 'PENDING', value: 0 },
  { label: 'APPROVED', value: 1 },
  { label: 'REJECTED', value: 2 },
];

export const TaskStatus = [
  { label: 'PENDING', value: 0 },
  { label: 'INPROGRESS', value: 1 },
  { label: 'COMPLETED', value: 2 },
];

export const fireInput = inputRef => inputRef?.current?.click();

export const parseError = errorObj => {
  if (![200, 201]?.includes(+errorObj?.code) && !errorObj?.error?.length) {
    return errorObj?.message;
  }
};

export function hasNoKeysOrValues(arr) {
  if (arr.length === 0) {
    return true;
  }

  for (let obj of arr) {
    if (Object.keys(obj).length === 0 || Object.values(obj).length === 0) {
      return true;
    }
  }

  return false;
}

export const checkAdmin = authData => {
  return ['ADMIN', 'SUPERADMIN']?.includes(
    authData?.STAFF_ROLE?.STAFF_ROLE_NAME,
  );
};

export const checkSuperAdmin = authData => {
  return ['SUPERADMIN']?.includes(authData?.STAFF_ROLE?.STAFF_ROLE_NAME);
};

const PAYMENT_SERVICES = { CASHFREE: "cashFree", DEFFAULT: "" }

export const fetchPaymentService = authData => {
  return PAYMENT_SERVICES?.[authData?.PAYMENT_SERVICE_NAME?.toUpperCase() || "DEFFAULT"];
};

export const formatAmount = (authData: JWTPayload | undefined, amount: string | number, isNA?: boolean): string => {
  let currencySymbol: string;
  let locale: string;

  switch (authData?.CURRENCY) {
    case 'USD':
      currencySymbol = '$';
      locale = 'en-US';
      break;
    case 'INR':
      currencySymbol = '₹';
      locale = 'en-IN';
      break;
    case 'EUR':
      currencySymbol = '€';
      locale = 'en-EU';
      break;
    case 'GBP':
      currencySymbol = '£';
      locale = 'en-GB';
      break;
    case 'JPY':
      currencySymbol = '¥';
      locale = 'ja-JP';
      break;
    case 'RUB':
      currencySymbol = '₽';
      locale = 'ru-RU';
      break;
    default:
      currencySymbol = '₹';
      locale = 'en-IN';
      break;
  }

  const numericAmount = convertNaNIntoNumber(amount);
  const isNegative = numericAmount < 0;
  const absoluteAmount = Math.abs(numericAmount);
  const formattedAmount = absoluteAmount.toLocaleString(locale);
  const prefix = isNegative ? '-' : '';

  return isNA ? (!!numericAmount ? `${prefix}${currencySymbol}\u00A0${formattedAmount}` : "NA") : `${prefix}${currencySymbol}\u00A0${formattedAmount}`;
};

export const truncateText = (text: string, len = 11) => {
  if (!text) return "";
  return text.length > len ? `${text.substring(0, len)}...` : text;
};

export const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDifference = today.getMonth() - birth.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export const directDownloadTheImage = async (url, filename = 'downloaded-image') => {
  try {
    // Extract file extension from URL
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const extensionMatch = pathname.match(/\.(\w+)(\?.*)?$/);
    const extension = extensionMatch ? extensionMatch[1] : 'jpg'; // Default to 'jpg' if no match

    const response = await fetch(url, { mode: 'cors' }); // May require CORS headers
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Append extension to filename
    const finalFilename = `${filename}.${extension}`;

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl); // Clean up memory
  } catch (error) {
    console.error('Download failed:', error);
  }
};


export const downloadFile = (url?, options?, isTarget?) => {
  const link = document.createElement('a');
  if (options) link.setAttribute('href', `options${encodeURIComponent(url)}`);
  if (isTarget) {
    link.setAttribute('target', `_blank`);
  }
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadMulti = async attachments => {
  try {
    const downloadPromises = attachments.map(async attachmentUrl => {
      const response = await axios.get(attachmentUrl, {
        responseType: 'blob',
      });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link: any = document.createElement('a');
      link.href = downloadUrl;
      link.download = attachmentUrl.split('/').pop();
      link.target = '_blank';
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    });

    await Promise.all(downloadPromises);
  } catch (error) {
    console.error('Error downloading attachments:', error);
  }
};

export function validatePassword(password) {
  if (password == null) {
    return 'Please enter a password';
  }
  return '';
  // if (password.length < 6) {
  //   return 'Password must be at least 6 characters';
  // }
  // if (!/[A-Z]/.test(password)) {
  //   return 'Password must contain at least one uppercase letter';
  // }
  // if (!/[a-z]/.test(password)) {
  //   return 'Password must contain at least one lowercase letter';
  // }
  // if (!/[0-9]/.test(password)) {
  //   return 'Password must contain at least one number';
  // }
  // if (!/[!@#$%^&*]/.test(password)) {
  //   return 'Password must contain at least one special character';
  // } else {
  //   return '';
  // }
}

export const ifNotPreviousYear = authData => {
  return (Boolean(authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_STATUS !== 'PREVIOUS') || window.location.pathname?.includes("/branch-setup/academicYear/academicYear"));
};

export function checkFileType(url, returnImageThing, returnFileThing) {
  // Get the file extension from the URL
  const fileExtension = url?.split('.')?.pop()?.toLowerCase();

  // List of common image file extensions
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];

  // Check if the file extension is in the imageExtensions array
  if (imageExtensions?.includes(fileExtension)) {
    return returnImageThing;
  } else {
    return returnFileThing;
  }
}

export async function generateThumbnail(videoUrl, width, height) {
  const video = document.createElement('video');
  video.src = videoUrl;

  const canvas = document.createElement('canvas');
  const ctx: any = canvas.getContext('2d');

  video.onloadedmetadata = () => {
    canvas.width = width || video.videoWidth;
    canvas.height = height || video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to data URL
    const thumbnailUrl = canvas.toDataURL('image/jpeg');

    // You can now use the thumbnail URL or display it in an <img> element
  };

  video.load();
}


export function isVideoUrl(url) {
  // List of video file extensions to check
  const videoExtensions = ['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'webm'];

  // Get the file extension from the URL
  const fileExtension = url?.split('.')?.pop()?.toLowerCase();

  // Check if the file extension is in the list of video extensions
  return videoExtensions?.includes(fileExtension);
}

export function maskMobileNumber(mobileNumber, authData?) {
  try {
    const dataInfo: string | null | any = EncryptedStorage.getItem('_DATA_');
    const info = JSON.parse(dataInfo);
    const accessInfo = getAuthAccessData(info || authData);
    if (!checkSuperAdmin(info || authData)) {
      if (dataInfo && mobileNumber) {
        const mobileMaskingAllowed = Boolean(accessInfo?.masking?.mobile);
        if (mobileMaskingAllowed) {
          // Check if the input is a valid mobile number (10 digits)
          return '******' + mobileNumber?.substring(6) || "";
        }
      } else {
        return "-"
      }
    }
    return mobileNumber;
  } catch (err) {
    console.log(err);
  }
}

export function maskaddharNumber(addharNumber) {
  const dataInfo: string | null | any = EncryptedStorage.getItem('_DATA_');
  const info = JSON.parse(dataInfo || '{}');
  const accessInfo = getAuthAccessData(info);
  if (!checkSuperAdmin(dataInfo)) {
    if (dataInfo) {
      const aadhaarMaskingAllowed = Boolean(accessInfo?.masking?.aadhaar);
      if (aadhaarMaskingAllowed) {
        // Check if the input is a valid addhar number (12 digits)
        return '******' + addharNumber.substring(8);
      }
    }
  }
  return addharNumber;
}

export function maskEmailAddress(email) {
  const dataInfo: string | null = EncryptedStorage.getItem('_DATA_');
  const info = JSON.parse(dataInfo || '{}');
  const accessInfo = getAuthAccessData(info);
  if (!checkSuperAdmin(dataInfo)) {
    if (dataInfo) {
      const emailMaskingAllowed = Boolean(accessInfo?.masking?.email);
      if (emailMaskingAllowed) {
        // Check if the input is a valid email address
        if (typeof email === 'string' && /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email?.trim())) {
          // Split the email address into the username and domain parts
          const [username, domain] = email.split('@');

          // Check if the username part has at least 2 characters
          if (username.length >= 2) {
            // Mask all characters in the username except the last 2 characters
            const maskedUsername = '*'.repeat(username.length - 2) + username.slice(-2);
            return maskedUsername + '@' + domain;
          } else {
            // If the username has less than 4 characters, return the original email
            return email;
          }
        } else {
          // If the input is not a valid email address, return an error message
          return !!email ? 'Invalid email address' : "-";
        }
      }
    }
  }
  return email;
}

export function goBack(url?) {
  // Go back to the previous page
  const PREVIOUS_PAGE = window.location.pathname;
  window.history.back();
  setTimeout(() => {
    const CURRENT_PAGE = window.location.pathname;
    if (String(PREVIOUS_PAGE) === String(CURRENT_PAGE) && !!url) {
      History.navigate(url);
    }
  }, 200);
}

export function checkSystemTimeFormatOtherThenChrome() {
  // Create a Date object with the current time
  const now = new Date();

  // Get the time string according to the user's locale
  const timeString = now.toLocaleTimeString();
  // Check if the time string contains "AM" or "PM"
  const is24hr = !/AM|PM/.test(timeString);
  return is24hr;
  // var date = new Date(Date.UTC(2012, 11, 12, 3, 0, 0));
  // var dateString = date.toLocaleTimeString();
  // const is24hr = !(dateString.match(/am|pm/i) || date.toString().match(/am|pm/i))
  // //apparently toLocaleTimeString() has a bug in Chrome. toString() however returns 12/24 hour formats. If one of two contains AM/PM execute 12 hour coding.
  // return is24hr
}

export function checkSystemTimeFormatInChrome() {
  let result = false;
  const date = new Date();
  const dateString = date.toLocaleTimeString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeStyle: 'short',
  });
  const is24hr = !(
    dateString.match(/am|pm/i) || date.toString().match(/am|pm/i)
  );
  return is24hr;
}

export function getTotalEnteriesCount(response) {
  return response?.[0]?.Total || response?.Total || 0;
}

export const getLabel = (key: string) => {
  if (key.includes('DATETIME')) {
    let words = key.split('_');

    for (let i = 0; i < words.length; i++) {
      words[i] =
        words[i].charAt(0).toUpperCase() + words[i].slice(1).toLowerCase();
    }

    let result = '';

    if (words[0]?.toUpperCase() === 'DATETIME') {
      result = 'Date Time';
    } else {
      result = words[0] + ' Date Time';
    }

    return result;
  }

  return key
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/(?: |\b)(\w)/g, function (key) {
      return key.toUpperCase();
    });
};

export function getImage(jsonStr) {
  let jsonObject: any = {};
  try {
    jsonObject = JSON.parse(jsonStr);
  } catch (err) {
    if (typeof (jsonStr) === "string") {
      jsonObject.data = jsonStr
    }
  }
  return jsonObject?.data?.startsWith('http')
    ? jsonObject?.data
    : getImage(jsonObject?.data);
}

export function numberToWordsINR(amount) {
  // Array of units up to hundred
  var units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  // Array of tens from twenty to ninety
  var tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  // Array of suffixes for large numbers
  var suffixes = ['', 'Thousand', 'Lakh', 'Crore'];

  // Function to convert numbers less than 1000 into words
  function convertLessThanOneThousand(number) {
    var words;

    if (number % 100 < 20) {
      words = units[number % 100];
      number = Math.floor(number / 100);
    } else {
      words = units[number % 10];
      number = Math.floor(number / 10);

      words = tens[number % 10] + ' ' + words;
      number = Math.floor(number / 10);
    }
    if (number === 0) return words;
    return units[number] + ' Hundred ' + words;
  }

  // Function to convert the given number into words
  function convertNumberToWords(number) {
    if (number === 0) return 'Zero';

    var words = '';
    for (var i = 0; number > 0; i++) {
      if (number % 1000 !== 0) {
        words = convertLessThanOneThousand(number % 1000) + ' ' + suffixes[i] + ' ' + words;
      }
      number = Math.floor(number / 1000);
    }
    return words.trim();
  }

  // Main function call
  return !!amount ? convertNumberToWords(amount) : "ZERO";
}

export const checkAccessControlResponse = (res) => {
  if (!!res?.ACCESS_CONTROL && !!res?.ACCESS_CONTROL?.length) {
    try {
      return Array.isArray(JSON.parse(res?.ACCESS_CONTROL))
    } catch (err) {
      console.log("Error: ", err);
      return false;
    }
  }
  return false;
}

export function formatCamelCaseStringIntoTitleCase(str) {
  // this function convert this kind of string "classHomework" and into this "Class Homework";
  let formattedStr = str?.replace(/([A-Z])/g, ' $1');
  formattedStr = formattedStr?.charAt(0)?.toUpperCase() + formattedStr?.slice(1);
  return formattedStr || "";
}

export function isValidInput(value: string, validateString?: boolean): boolean {
  if (validateString && /[0-9]/.test(value ?? '')) {
    return false;
  }
  return (
    value?.trim() !== '' &&
    /^[^\w\s.]/.test(value) === false &&
    /[^\w\s.]$/.test(value) === false
  );
}


export const convertNaNIntoNumber = (number) => {
  try {
    return isNaN(number) ? 0 : Number(number);
  } catch (err) {
    return 0
  }
}

export function capitalizeString(str) {
  // Split the string into an array of words
  let words = str?.split(' ');

  // Capitalize the first letter of each word
  for (let i = 0; i < words?.length; i++) {
    words[i] = words?.[i]?.charAt(0).toUpperCase() + words?.[i]?.slice(1)?.toLowerCase();
  }

  // Join the words back into a single string
  return words?.join(' ');
}

function generateFinalPath(obj) {
  // Remove * from parentPath
  const parentPathWithoutAsterisk = obj?.parentPath?.replace('*', '');

  // Concatenate parentPath, route, and url
  const finalPath = `${parentPathWithoutAsterisk}${obj?.route}/${obj?.url}/create`;

  return finalPath;
}

export const dynamicUrlNavigation = (basicPath) => {
  History.navigate(dynamicUrlCreation(basicPath));
}

export const dynamicUrlCreation = (basicPath) => {
  let result: any = [];
  MODULES.forEach((module: any) => {
    if (module.subModules.length > 0) {
      module.subModules.forEach(subModule => {
        result.push({
          parentModule: module.module,
          parentName: module.name,
          parentPath: module.path,
          ...subModule
        });
      });
    } else {
      result.push(module);
    }
  });
  return generateFinalPath(result?.find(item => item?.url === basicPath));
}

export function extractFilename(url: string): string {
  const urlParts: string[] = url?.split('/');
  const filenameWithExtension: string = urlParts?.[urlParts?.length - 1];
  const filename: string = filenameWithExtension?.split('.')[0];
  return filename;
}

export const replaceNullOrEmptyWithUndefined = (obj) => {
  Object.keys(obj).forEach(key => {
    if (key !== "LAST_NAME") {
      if (obj[key] === null || obj[key] === '') {
        obj[key] = undefined;
      }
    }
  });
};

// export const updateUserFilterLocalStorage = (newValue, key) => {
//   const currentValue = JSON.parse(EncryptedStorage.getItem('USER_FILTER_PREFRENCES') || '{}');
//   const updatedValue = { ...currentValue, ...newValue };
//   EncryptedStorage.setItem('USER_FILTER_PREFRENCES', JSON.stringify(updatedValue));
// };

export const updateUserFilterLocalStorage = (newValue, key) => {
  const currentValue = JSON.parse(EncryptedStorage.getItem('USER_FILTER_PREFRENCES') || '{}');

  // Filter keys that include the given key
  const filteredValue = Object.keys(currentValue)
    .filter(k => k.includes(key))
    .reduce((obj, k) => {
      obj[k] = currentValue[k];
      return obj;
    }, {});

  // Merge the filteredValue with the newValue
  const updatedValue = { ...filteredValue, ...newValue };

  EncryptedStorage.setItem('USER_FILTER_PREFRENCES', JSON.stringify(updatedValue));
};

export const getUserFilterLocalStorage = (key) => {
  return JSON.parse(EncryptedStorage.getItem('USER_FILTER_PREFRENCES') || '{}')?.[key] || "";
};

export const removeUserFilterLocalStorage = () => {
  EncryptedStorage.removeItem('USER_FILTER_PREFRENCES')
};

export const ErrorMessageFunction = ({ basicPath, name, customMessage, customHandler, externalURL }: { basicPath?: string, name?: string, customMessage?: string, customHandler?: string, externalURL?: string }) => {
  const handleClick = () => {
    if (!!externalURL) {
      return window.open(externalURL, '_blank')
    }
    return !customHandler ? dynamicUrlNavigation(basicPath) : History.navigate(customHandler);
  }
  const message = customMessage
    ? customMessage
    : `${capitalizeString(name || basicPath)} Not Found!`;
  const spanElement = React.createElement(
    'span',
    {
      onClick: handleClick,
      className: 'cursor-pointer font-semibold underline-offset-2',
    },
    `Click here to create ${(name || basicPath)?.toLowerCase()}.`
  );

  return React.createElement('p', null, `${message} `, spanElement);
};


export function getDatesBetween(startDate: string, endDate: string): Date[] {
  const dateArray: Date[] = [];
  let currentDate = new Date(startDate);
  while (currentDate <= new Date(endDate)) {
    dateArray.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dateArray;
}


// Function to extract all dates from the holiday array
export function extractAllDates(data: BranchHoliday[]): Date[] {
  let allDates: Date[] = [];

  data.forEach((item) => {
    const startDate = item.START_DATE;
    const endDate = item.END_DATE;

    const dates = getDatesBetween(startDate, endDate);
    allDates = [...allDates, ...dates];
  });

  return allDates;
}


export const BRANCH_SETUP_INFO = [
  { route: 'academicYear', info: '' },
  { route: 'branch', info: 'View the Branch details' },
  { route: 'branchDocument', info: '' },
  { route: 'branchEnquiry', info: ' View the details of Admission enquiry eg: admission,visitor list,enquiry(We capture all information about visitors ' },
  { route: 'branchHoliday', info: 'We gather comprehensive information about holidays, including occasions like Dasara and Republic day.' },
  { route: 'staffLeaveType', info: 'We capture the staff leave information in a systematically manner' },
  { route: 'branchShift', info: '' },
  { route: 'visitor', info: 'Recording all details about visitor activity to enhance security measures, including visitor information, purpose of visit, and maintaining records of in and out times.' },
  { route: 'class', info: '' },
  { route: 'subject', info: 'We capture information to showcase all details about subjects, such as Telugu and Hindi.' },
  { route: 'subjectGroup', info: '' },
  { route: 'branchPhotoGallery', info: ' upload photos' },
  { route: 'branchPhoto', info: '' },
  { route: 'branchActivityLog', info: '' },
  { route: 'branchRole', info: '' }
]

export const USER_SETUP_INFO = [
  { route: 'student', info: '' },
  { route: 'studentDocument', info: '' },
  { route: 'staff', info: 'We gather all information about staff, including their teaching subjects, personal details, and more.' },
  { route: 'staffAttendance', info: '' },
  { route: 'staffLeaveBalance', info: 'We capture staff available leave balance ' },
  { route: 'staffLeave', info: 'We capture the staff leave information in a systematically manner' },
  { route: 'staffTasks', info: 'We document all information related to staff tasks, including task descriptions, assigned staff members, and deadlines.' },
  { route: 'parent', info: '' },
  { route: 'parentFeedback', info: '' }
]

export const CLASS_SETUP_INFO = [
  { route: 'classSubject', info: 'Create class subject eg: class 1- Telugu, Hindi' },
  { route: 'classTimetable', info: 'Map the subject to the staff and create class timetable' },
  { route: 'classHomework', info: ' Map the subject to the staff and create the class homework' },
  { route: 'lessonPlan', info: 'Map the subject to the staff and create lesson plan for subject' },
  { route: 'onlineClass', info: 'Map subject to the staff and after that create online class' },
  { route: 'studentAttendance', info: 'Marks attendance for student eg: present, absent' },
  { route: 'studentLeave', info: 'Create staff leave balance after that create staff leave' },
  { route: 'subjectHomework', info: 'Map subject to the staff and after that create homework for subject' },
  { route: 'studentPromote', info: 'Promote (or) Demote student one class to another class eg: Shanvikaa promoted to class 2 to class 3' },
  { route: 'classPromote', info: 'Promote (or) Demote class one academic year to another academic year' },
  { route: 'staffClass', info: 'Add subject to staff eg: Telugu - Bhargavi' },
  { route: 'parentTeacherMeeting', info: 'Create the ptm for student' }
]

export const EXAM_SETUP_INFO = [
  { route: 'examGradeDetail', info: 'Create exam grades eg :  marks range 45 -50 then grade will be - A1,marks range 41-44 then  grade will be A2 then only print the report card' },
  { route: 'examType', info: 'Create exam type eg: Internal, External' },
  { route: 'exam', info: 'Create exam eg: Quaterly, Half year' },
  { route: 'examClass', info: 'Create exam for class after creating exam and exam type' },
  { route: 'examStudentResult', info: 'Enter the marks for student class exam eg : Telugu- 45' },
  { route: 'examSubjectStudentResult', info: '' },
  { route: 'hallticket', info: 'Generate hall ticket for student class exam after entering the start and end time, maximum marks and also passing marks' },
  { route: 'report', info: 'Generate report card for student class exam after entering marks and create grades' },
  { route: 'ExamHallTicketSetting', info: '' },
  { route: 'ExamReportCardSetting', info: '' }
]

export const FEE_SETUP_INFO = [
  { route: 'feeCategory', info: 'Create fee category names eg: Tuition Fee, Bus fee these are the categories for class and student ' },
  { route: 'feeSchedule', info: 'Create schedule eg: may, June, July. Fee schedule means fixed fee amount for schedule' },
  { route: 'feeClassCategory', info: 'First create after category and schedule, the fee class category should be created eg: june:2000.july: 2000' },
  { route: 'feeStudentCategory', info: 'Create after fee class category, the fee student category should be created eg: Tution fee: 20000' },
  { route: 'feeStudentConcession', info: 'Create after fee student category, then add fee student concession.' },
  { route: 'feeStudentPayment', info: 'View the class payment deatails' },
  { route: 'feeStudentRequest', info: 'View the fee student request deatails' },
  { route: 'accountHeadCategory', info: 'Create account category names eg: stationary, xerox' },
  { route: 'accountTransaction', info: 'Create payment transaction deatails' },
  { route: 'branchBalanceSheet', info: 'View the income and expenses deatails' }
];

export const COMMUNICATION_SETUP_INFO = [
  { route: 'branchCommunicationDashboard', info: '' },
  { route: 'branchCommunicationSetting', info: '' },
  { route: 'callAnnouncement', info: 'Create call announcement(create voice announcements ' },
  { route: 'CallCampaign', info: 'Send call for students(Crate schedulers for sending calls to parents automatically)' },
  { route: 'smsTemplate', info: 'Create sms templates(approved templates for sending parents like attendance ,home work ,announcements and fee etc.)' },
  { route: 'smsCampaign', info: 'Send sms for students(Create schedulers for sending sms to parents automatically)' },
  { route: 'ChatThread', info: 'Create chat thread(Super admin & admin to communicate staff for internally)' },
  { route: 'CommunicationRecharge', info: '' },
  { route: 'admissionCampaignNumber', info: '' },
  { route: 'AdmissionCampaign', info: '' },
  { route: 'smAppCampaign', info: 'Create and schedule App Push Notification campaigns for students.' },
];

export const TRANSPORT_SETUP_INFO = [
  { route: 'transportDriver', info: 'Create driver ' },
  { route: 'TransportRoute', info: 'Create route' },
  { route: 'TransportVehicle', info: 'Map the driver and route for bus' },
  { route: 'TransportPickupPoint', info: 'Map the route for pickup point and add transport fee' },
  { route: 'TransportPickupPointConcession', info: 'Create student pickup point and add concession for concession' },
  { route: 'TransportVehicleTrip', info: '' },
  { route: 'mapPickupPointStudents', info: 'View the student pickup points' }
];

export function resetModules(arr: any = []) {
  return arr?.map(obj => {
    // Create a copy of the object to avoid mutating the original object
    const newObj = { ...obj };

    // Convert top-level keys
    for (let key in newObj) {
      if (newObj[key] === true) {
        newObj[key] = false;
      }
    }

    // Convert subModules keys if subModules array exists
    if (Array.isArray(newObj.subModules) && newObj.subModules.length > 0) {
      newObj.subModules = newObj.subModules.map(subObj => {
        const newSubObj = { ...subObj };
        for (let subKey in newSubObj) {
          if (newSubObj[subKey] === true) {
            newSubObj[subKey] = false;
          }
        }
        return newSubObj;
      });
    }

    return newObj;
  });
}

export const MODULES = [
  {
    module: 'Dashboard',
    name: 'Dashboard',
    path: '/dashboard',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'SupportTicket',
    name: 'Support Ticket',
    path: '/support-ticket',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'UserProfileSettings',
    name: 'User Profile Settings',
    path: '/user-profile',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'HallticketGenerator',
    name: 'Hall Ticket Generator',
    path: '/generator',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'ReportGenerator',
    name: 'Report Generator',
    path: '/report',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'CumulativeReportGenerator',
    name: 'Cumulative Report Generator',
    path: '/cumulative-report',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'ReportCardPreview',
    name: 'Report Card Preview',
    path: '/reportcard-preview',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'HallticketPreview',
    name: 'Hall ticket Preview',
    path: '/hallticket-preview',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'Masking_NOTAROUTER',
    name: 'Masking',
    path: '/Masking_NOTAROUTER',
    subModules: [
      { name: 'Allowed', url: 'SubMasking_NOTAROUTER', route: 'SubMasking_NOTAROUTER', hasAccess: true, mobile: true, email: true, aadhaar: true }
    ],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'BonafiedCertificate',
    name: 'Bonafied Certificate',
    path: '/certificates',
    subModules: [
      { name: 'Allowed', url: 'BonafiedCertificate', route: 'BonafiedCertificate', hasAccess: true, upsert: true, delete: true, view: true }
    ],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'StudyCertificate',
    name: 'Study Certificate',
    path: '/certificates',
    subModules: [
      { name: 'Allowed', url: 'STUDENT_CERTIFICATE', route: 'STUDENT_CERTIFICATE', hasAccess: true, upsert: true, delete: true, view: true }
    ],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'TransferCertificate',
    name: 'Transfer Certificate',
    path: '/certificate',
    subModules: [
      { name: 'Allowed', url: 'TransferCertificate', route: 'TransferCertificate', hasAccess: true, upsert: true, delete: true, view: true }
    ],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'DashboardComponents',
    name: 'Dashboard Components',
    path: '/dashboard/*',
    subModules: [
      { name: 'Finance', url: 'Finance', route: 'Finance', hasAccess: false }
    ],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'BranchSetup',
    path: '/branch-setup/*',
    name: 'Branch',
    subModules: [
      { name: 'Academic Year', url: 'academicYear', route: 'academicYear', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Branch Info', url: 'branch', route: 'branch', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Documents', url: 'branchDocument', route: 'branchDocument', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Holidays', url: 'branchHoliday', route: 'branchHoliday', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Staff Leave Types', url: 'staffLeaveType', route: 'staffLeaveType', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Shifts', url: 'branchShift', route: 'branchShift', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Visitors', url: 'visitor', route: 'visitor', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Classes', url: 'class', route: 'class', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Subjects', url: 'subject', route: 'subject', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Subject Groups', url: 'subjectGroup', route: 'subjectGroup', hasAccess: true, upsert: true, delete: true, view: true },
      // { name: 'Events', url: 'event', route: 'branchEvents', hasAccess: true, upsert: true, delete: true, view: true },
      // { name: 'Events Details', url: 'eventDetail', route: 'branchEvents', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Birthdays', url: 'branchPhotoGallery', route: 'branchPhotoGallery', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Branch Photo', url: 'branchPhoto', route: 'branchPhoto', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Role Access', url: 'branchRole', route: 'branchRole', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Website Templates', url: 'masterTemplate', route: 'websiteTemplate', hasAccess: true, upsert: true, delete: true, view: true },
      // { name: 'Website Content', url: 'Website', route: 'Website', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'UserSetup',
    name: 'User',
    path: '/user-setup/*',
    subModules: [
      { name: 'Students', url: 'student', route: 'student', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Documents', url: 'studentDocument', route: 'studentDocument', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Transfer Students', url: 'transferStudents', route: 'transferStudents', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student ID Cards', url: 'studentIdCard', route: 'studentIdCard', hasAccess: true, upsert: true, delete: true, view: true },

      { name: 'Staffs', url: 'staff', route: 'staff', hasAccess: true, upsert: false, delete: false, view: true },
      { name: 'Staff Attendance', url: 'staffAttendance', route: 'staffAttendance', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Staff Tasks', url: 'staffTasks', route: 'staffTasks', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Staff Leave Balance', url: 'staffLeaveBalance', route: 'staffLeaveBalance', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Staff Leaves', url: 'staffLeave', route: 'staffLeave', hasAccess: true, upsert: true, delete: true, view: true },

      { name: 'Parents', url: 'parent', route: 'parent', hasAccess: true, upsert: true, delete: true, view: true },
      // { name: 'Parent Feedback', url: 'parentFeedback', route: 'parentFeedback', hasAccess: true, upsert: true, delete: true, view: true },

    ],
    hasAccess: true,
  },
  {
    module: 'ClassSetup',
    name: 'Class',
    path: '/class-setup/*',
    subModules: [
      { name: 'Class Subjects', url: 'classSubject', route: 'classSubject', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Time Table', url: 'classTimetable', route: 'classTimetable', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Class Homework', url: 'classHomework', route: 'classHomework', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Lesson Plan', url: 'lessonPlan', route: 'lessonPlan', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Online Class', url: 'onlineClass', route: 'onlineClass', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Attendance', url: 'studentAttendance', route: 'studentAttendance', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Leaves', url: 'studentLeave', route: 'studentLeave', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Subject Homework', url: 'classHomework', route: 'subjectHomework', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Reassign Class', url: 'student', route: 'studentPromote', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Class Promote', url: 'student', route: 'classPromote', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Staff Class', url: 'staffClass', route: 'staffClass', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Parent Teacher Meeting', url: 'parentTeacherMeeting', route: 'parentTeacherMeeting', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'ExamSetup',
    name: 'Exam',
    path: '/exam-setup/*',
    subModules: [
      { name: 'Exam Grade Detail', url: 'examGradeDetail', route: 'examGradeDetail', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Exam Type', url: 'examType', route: 'examType', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Exam Parameter', url: 'examParameter', route: 'examParameter', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Exam', url: 'exam', route: 'exam', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Class Exam', url: 'examClass', route: 'examClass', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Result', url: 'examStudentResult', route: 'examStudentResult', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Subject Wise Result', url: 'examStudentResult', route: 'examSubjectStudentResult', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Hall Ticket', url: 'hallticket', route: 'hallticket', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Report Card', url: 'report', route: 'report', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Cumulative Structure', url: 'cumulativeStructure', route: 'cumulativeStructure', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Cumulative Report Card', url: 'cumulativeReport', route: 'cumulativeReport', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Class Exam Analysis', url: 'classExamAnalysis', route: 'classExamAnalysis', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Hall Ticket Settings', url: 'ExamHallTicketSetting', route: 'ExamHallTicketSetting', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Report Card Settings', url: 'ExamReportCardSetting', route: 'ExamReportCardSetting', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'FeeSetup',
    name: 'Fee',
    path: '/fee-setup/*',
    subModules: [
      { name: 'Categories', url: 'feeCategory', route: 'feeCategory', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Schedules', url: 'feeSchedule', route: 'feeSchedule', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Class Categories', url: 'feeClassCategory', route: 'feeClassCategory', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Fee', url: 'feeStudentCategory', route: 'feeStudentCategory', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Concessions', url: 'feeStudentConcession', route: 'feeStudentConcession', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Payments', url: 'feeStudentPayment', route: 'feeStudentPayment', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Deleted Payments', url: 'feeStudentPayment', route: 'deletedList', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Arrears', url: 'feeStudentArrears', route: 'feeStudentArrears', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Delay Requests', url: 'feeStudentRequest', route: 'feeStudentRequest', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Account Categories', url: 'accountHeadCategory', route: 'accountHeadCategory', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Account Transactions', url: 'accountTransaction', route: 'accountTransaction', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Balance Sheet', url: 'branchBalanceSheet', route: 'branchBalanceSheet', hasAccess: true, upsert: true, delete: true, view: true }
    ],
    hasAccess: true,
  },
  {
    module: 'CommunicationSetup',
    name: 'Communication',
    path: '/communication-setup/*',
    subModules: [
      { name: 'Dashboard', url: 'branchCommunicationSetting', route: 'branchCommunicationDashboard', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Call Announcement', url: 'callAnnouncement', route: 'callAnnouncement', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Call Campaign', url: 'CallCampaign', route: 'CallCampaign', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'SMS Template', url: 'smsTemplate', route: 'smsTemplate', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'SMS Campaign', url: 'smsCampaign', route: 'smsCampaign', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'WhatsApp Template', url: 'whatsappTemplate', route: 'whatsappTemplate', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'WhatsApp Campaign', url: 'whatsAppCampaign', route: 'whatsAppCampaign', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'SM App Campaign', url: 'appCampaign', route: 'smAppCampaign', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Internal Chat', url: 'ChatThread', route: 'ChatThread', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Communication Recharge', url: 'CommunicationRecharge', route: 'CommunicationRecharge', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'IVR Parent Feedback', url: 'ivrParentFeedback', route: 'ivrParentFeedback', hasAccess: true, upsert: false, delete: true, view: false },
      { name: 'Settings', url: 'branchCommunicationSetting', route: 'branchCommunicationSetting', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'TransportSetup',
    name: 'Transport',
    path: '/transport-setup/*',
    subModules: [
      { name: 'Drivers', url: 'transportDriver', route: 'transportDriver', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Routes', url: 'TransportRoute', route: 'TransportRoute', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Vehicles', url: 'TransportVehicle', route: 'TransportVehicle', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Pickup Points', url: 'TransportPickupPoint', route: 'TransportPickupPoint', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Pickup Point Concessions', url: 'TransportPickupPointConcession', route: 'TransportPickupPointConcession', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Trip & Live Tracking', url: 'TransportVehicleTrip', route: 'TransportVehicleTrip', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Student Pickup Points', url: 'mapPickupPointStudents', route: 'mapPickupPointStudents', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'AdmissionSetup',
    name: 'Admission Enquiry',
    path: '/admission-setup/*',
    subModules: [
      { name: 'Dashboard', url: 'enquiry', route: 'dashboardEnquiry', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Campaign Template', url: 'admissionCampaignTemplate', route: 'admissionCampaignTemplate', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Enquiry List', url: 'branchEnquiry', route: 'branchEnquiry', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Enquiry Campaign', url: 'AdmissionCampaign', route: 'AdmissionCampaign', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'SocialHub',
    name: 'Social Hub',
    path: '/social-hub/*',
    subModules: [
      { name: 'Link Profiles', url: 'socialHub', route: 'authSocialHub', hasAccess: false, upsert: true, delete: true, view: true },
      { name: 'Default Events', url: 'masterEvent', route: 'eventSetup', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Events', url: 'event', route: 'events', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
  },
  {
    module: 'DigitalMarketing',
    name: 'Growth & Excellence',
    path: '/digital-marketing',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'AgenticTasks',
    name: 'Agentic Tasks',
    path: '/agentic-tasks/*',
    subModules: [
      { name: 'Automations', url: 'automations', route: 'automations', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'Templates', url: 'templates', route: 'templates', hasAccess: true, upsert: true, delete: true, view: true },
      { name: 'History', url: 'history', route: 'history', hasAccess: true, upsert: true, delete: true, view: true },
    ],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'InternalServerErrorPage',
    name: 'Internal Server Error',
    path: '/server-error',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'StudentSummary',
    name: 'Student Summary',
    path: '/student-summary/:id/:studentclassid',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  // {
  //   module: 'ParentSummary',
  //   name: 'Parent Summary',
  //   path: '/parent-summary/:id',
  //   subModules: [],
  //   hasAccess: true,
  //   upsert: true,
  //   delete: true,
  //   view: true
  // },
  {
    module: 'StaffSummary',
    name: 'Staff Summary',
    path: '/staff-summary/:id',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  {
    module: 'ClassSummary',
    name: 'Class Summary',
    path: '/class-summary/:id',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
  // {
  //   module: 'AdmissionCampaign',
  //   name: 'Admission Campaign',
  //   path: '/admission-campaign/:id',
  //   subModules: [],
  //   hasAccess: true,
  //   upsert: true,
  //   delete: true,
  //   view: true,
  // },
  {
    module: 'NotFoundPage',
    name: 'Page Not Found',
    path: '/*',
    subModules: [],
    hasAccess: true,
    upsert: true,
    delete: true,
    view: true,
  },
];

export const MODULE_NAMES = [
  { name: "ACCOUNT", value: "ACCOUNT" },
  { name: "ACCOUNTS", value: "ACCOUNTS" },
  { name: "ACTIVITY LOG", value: "ACTIVITY_LOG" },
  { name: "ADMISSION CAMPAIGN", value: "ADMISSION_CAMPAIGN" },
  { name: "ATTENDANCE", value: "ATTENDANCE" },
  { name: "BRANCH", value: "BRANCH" },
  { name: "BRANCH COMMUNICATION SETTING", value: "BRANCH_COMMUNICATION_SETTING" },
  { name: "BRANCH DOCUMENT", value: "BRANCH_DOCUMENT" },
  { name: "BRANCH DOCS", value: "BRANCH_DOCS" },
  { name: "BRANCH ENQUIRY", value: "BRANCH_ENQUIRY" },
  { name: "BRANCH HOLIDAY", value: "BRANCH_HOLIDAY" },
  { name: "BRANCH ROLE", value: "BRANCH_ROLE" },
  { name: "BRANCH SETUP", value: "BRANCH_SETUP" },
  { name: "CALL", value: "CALL" },
  { name: "CALL CAMPAIGN", value: "CALL_CAMPAIGN" },
  { name: "CHAT GROUP", value: "CHAT_GROUP" },
  { name: "CHAT THREAD", value: "CHAT_THREAD" },
  { name: "CLASS", value: "CLASS" },
  { name: "CLASS STAFF", value: "CLASS_STAFF" },
  { name: "CLASS TIMETABLE", value: "CLASS_TIMETABLE" },
  { name: "COMMUNICATION MODULE", value: "COMMUNICATION_MODULE" },
  { name: "COMMUNICATION RECHARGE", value: "COMMUNICATION_RECHARGE" },
  { name: "DASHBOARD", value: "DASHBOARD" },
  { name: "EVENT", value: "EVENT" },
  { name: "EXAM", value: "EXAM" },
  { name: "FEE", value: "FEE" },
  { name: "HOMEWORK", value: "HOMEWORK" },
  { name: "ONLINE CLASS", value: "ONLINE_CLASS" },
  { name: "PARENT", value: "PARENT" },
  { name: "PARENT TEACHER MEETING", value: "PARENT_TEACHER_MEETING" },
  { name: "PARENT_FEEDBACK", value: "PARENT_FEEDBACK" },
  { name: "REFER US", value: "REFER_US" },
  { name: "SETTINGS", value: "SETTINGS" },
  { name: "SMS", value: "SMS" },
  { name: "SMS CAMPAIGN", value: "SMS_CAMPAIGN" },
  { name: "STAFF", value: "STAFF" },
  { name: "STAFF CLASS", value: "STAFF_CLASS" },
  { name: "STAFF LEAVE", value: "STAFF_LEAVE" },
  { name: "STATISTICS", value: "STATISTICS" },
  { name: "STUDENT", value: "STUDENT" },
  { name: "SUBJECT", value: "SUBJECT" },
  { name: "SUBJECT GROUP", value: "SUBJECT_GROUP" },
  { name: "SUMMARY", value: "SUMMARY" },
  { name: "TICKET", value: "TICKET" },
  { name: "TRANSPORT", value: "TRANSPORT" },
  { name: "USER", value: "USER" },
  { name: "VISITORS", value: "VISITORS" }
];


// to fetch all the unique dynamic parameters

/* let list = [
    {
        "SubjectId": 7,
        "ShouldGroupSubject": false,
        "SubjectType": "Regular",
        "ParameterResults": [
            {
                "PARAMETER_ID": 7,
                "SubjectId": 7,
                "PARAMETER_NAME": "SECTION - E",
                "PARAMETER_MARKS": 20,
                "PARAMETER_OBTAINED_MARKS": 0
            },
            {
                "PARAMETER_ID": 4,
                "SubjectId": 7,
                "PARAMETER_NAME": "SECTION - D",
                "PARAMETER_MARKS": 30,
                "PARAMETER_OBTAINED_MARKS": 0
            }
        ],
    },
    {
        "SubjectId": 13,
        "ShouldGroupSubject": true,
        "SubjectType": "Regular",
    }
];

// Create a set to store unique PARAMETER_IDs
let uniqueParametersSet = new Set();
let uniqueParameters = [];

// Iterate over the list and extract ParameterResults if available
list.forEach(item => {
    if (item.ParameterResults) {
        item.ParameterResults.forEach(param => {
            // Check if the PARAMETER_ID has already been added to the set
            if (!uniqueParametersSet.has(param.PARAMETER_ID)) {
                uniqueParametersSet.add(param.PARAMETER_ID);
                // Add the PARAMETER_NAME and PARAMETER_ID to the result list
                uniqueParameters.push({
                    PARAMETER_NAME: param.PARAMETER_NAME,
                    PARAMETER_ID: param.PARAMETER_ID,
                    SubjectId: param.SubjectId
                });
            }
        });
    }
});

console.log(uniqueParameters);

*/
